import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Upload, X, CheckCircle2, Users, Car, Landmark, Send, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, unwrap } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";

const LANGUAGES = [
  { code: "en", label: "English" }, { code: "es", label: "Español" },
  { code: "pt", label: "Português" }, { code: "fr", label: "Français" }, { code: "it", label: "Italiano" },
];

const DAYS = [
  { code: "monday", label: "Monday" }, { code: "tuesday", label: "Tuesday" }, { code: "wednesday", label: "Wednesday" },
  { code: "thursday", label: "Thursday" }, { code: "friday", label: "Friday" }, { code: "saturday", label: "Saturday" },
  { code: "sunday", label: "Sunday" },
];

const PREFERRED_HOURS = [
  { code: "mornings", label: "Mornings (7 AM – 12 PM)" }, { code: "midday", label: "Midday (11 AM – 3 PM)" },
  { code: "afternoons", label: "Afternoons (12 PM – 5 PM)" }, { code: "evenings", label: "Evenings (5 PM – 9 PM)" },
  { code: "full_day", label: "Full day (7 AM – 5 PM)" }, { code: "flexible", label: "Flexible / Any time" },
];

const WEEKLY_COMMITMENT = [
  { code: "full_time", label: "Full-time (40+ hours/week)" },
  { code: "part_time", label: "Part-time (20–39 hours/week)" },
  { code: "flexible", label: "Flexible / As needed (less than 20 hours/week)" },
];

const TRANSPORTATION_TYPE = [
  { code: "personal_vehicle", label: "Personal vehicle" }, { code: "family_friend", label: "Ride from family/friend" },
  { code: "public", label: "Public transportation" }, { code: "other", label: "Other" },
];

const EMPTY = {
  full_name: "", photo_url: "", phone: "", email: "", country: "US", city: "",
  languages: [], specialties: [], bio: "", notes: "", is_active: true,
  available_days: [], preferred_hours: "", weekly_commitment: "",
  has_reliable_transportation: false, transportation_type: "", transportation_other: "",
};

// Center-crop to a square and downscale to <=512px, returns a JPEG data URL.
function fileToSquareDataUrl(file, size = 512) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2, sy = (img.height - side) / 2;
        const canvas = document.createElement("canvas");
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AdminCleaners() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState("");
  const [active, setActive] = useState("");
  const [availableDay, setAvailableDay] = useState("");
  const [weeklyCommitment, setWeeklyCommitment] = useState("");
  const [hasTransport, setHasTransport] = useState("");
  const [tasks, setTasks] = useState([]);
  const [editing, setEditing] = useState(null); // cleaner obj or {} for new

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (country) params.set("country", country);
      if (active) params.set("is_active", active);
      if (availableDay) params.set("available_day", availableDay);
      if (weeklyCommitment) params.set("weekly_commitment", weeklyCommitment);
      if (hasTransport) params.set("has_reliable_transportation", hasTransport);
      const r = await api.get(`/admin/cleaners?${params.toString()}`);
      setItems(unwrap(r).items || []);
    } finally { setLoading(false); }
  }, [country, active, availableDay, weeklyCommitment, hasTransport]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/pricing/active").then((r) => setTasks(unwrap(r)?.tasks || [])).catch(() => {}); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.full_name}?`)) return;
    await api.delete(`/admin/cleaners/${c.id}`);
    toast.success("Cleaner deleted");
    load();
  };

  return (
    <div className="space-y-5" data-testid="admin-cleaners">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Cleaners</h1>
          <p className="text-[#4B5563] text-sm mt-1">{items.length} cleaner(s) in your network</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="add-cleaner-btn">
          <Plus className="w-4 h-4 mr-2" /> Add cleaner
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={country} onChange={(e) => setCountry(e.target.value)} className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="cleaners-country-filter">
          <option value="">All countries</option>
          {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name_en}</option>)}
        </select>
        <select value={active} onChange={(e) => setActive(e.target.value)} className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="cleaners-active-filter">
          <option value="">All statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <select value={availableDay} onChange={(e) => setAvailableDay(e.target.value)} className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="cleaners-day-filter">
          <option value="">Any day</option>
          {DAYS.map((d) => <option key={d.code} value={d.code}>Available {d.label}</option>)}
        </select>
        <select value={weeklyCommitment} onChange={(e) => setWeeklyCommitment(e.target.value)} className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="cleaners-commitment-filter">
          <option value="">Any commitment</option>
          {WEEKLY_COMMITMENT.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        <select value={hasTransport} onChange={(e) => setHasTransport(e.target.value)} className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="cleaners-transport-filter">
          <option value="">Any transportation</option>
          <option value="true">Reliable transport</option>
          <option value="false">No reliable transport</option>
        </select>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border border-[#E5E7EB] text-center py-16 text-[#4B5563]">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-50" /> No cleaners yet. Add your first one.
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cleaners-grid">
          {items.map((c) => (
            <Card key={c.id} className="rounded-2xl border border-[#E5E7EB] p-5" data-testid={`cleaner-card-${c.id}`}>
              <div className="flex items-start gap-3">
                <Avatar src={c.photo_url} name={c.full_name} size={56} />
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold truncate">{c.full_name}</div>
                  <div className="text-xs text-[#4B5563]">{[c.city, c.country].filter(Boolean).join(", ") || "—"}</div>
                  <div className="text-xs text-[#9CA3AF] mt-0.5">{c.bookings_completed} completed</div>
                </div>
                <Badge className={`border font-medium ${c.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-100 text-stone-600 border-stone-200"}`}>
                  {c.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
              {c.bio && <p className="text-sm text-[#4B5563] mt-3 line-clamp-2">{c.bio}</p>}
              <PayoutRow cleaner={c} onChanged={load} />
              {(c.available_days?.length > 0 || c.weekly_commitment) && (
                <div className="text-xs text-[#4B5563] mt-2 flex flex-wrap gap-1.5" data-testid={`cleaner-availability-${c.id}`}>
                  {(c.available_days || []).map((d) => (
                    <span key={d} className="px-1.5 py-0.5 rounded bg-[#F5F5F4] border border-[#E5E7EB] capitalize">{d.slice(0, 3)}</span>
                  ))}
                  {c.has_reliable_transportation && <span className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700"><Car className="w-3 h-3" /></span>}
                </div>
              )}
              {c.user_id && <ResendKitRow cleaner={c} />}
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="rounded-xl flex-1" onClick={() => setEditing(c)} data-testid={`edit-cleaner-${c.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" className="rounded-xl text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => remove(c)} data-testid={`delete-cleaner-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <CleanerForm key={editing?.id || (editing ? "new" : "closed")} cleaner={editing} tasks={tasks} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

const PAYOUT_META = {
  not_started: { label: "Not started", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  in_progress: { label: "In progress", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  ready: { label: "Ready", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

/** Payout onboarding status + "Invite to set up payouts" (Fase 3, Bloque A). */
function PayoutRow({ cleaner, onChanged }) {
  const [busy, setBusy] = useState(false);
  const meta = PAYOUT_META[cleaner.payout_status] || PAYOUT_META.not_started;

  const invite = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/cleaners/${cleaner.id}/invite-payouts`);
      const d = unwrap(r);
      toast.success(d?.email_sent
        ? `Payout setup invite sent to ${cleaner.email}`
        : "Stripe account ready, but the email could not be sent");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not send the payout invite");
    } finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      await api.get(`/admin/cleaners/${cleaner.id}/payout-status`);
      toast.success("Payout status refreshed from Stripe");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not refresh from Stripe");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#F1F1F0]" data-testid={`cleaner-payout-row-${cleaner.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Payout status</span>
        <Badge className={`border font-medium ${meta.cls}`} data-testid={`cleaner-payout-badge-${cleaner.id}`}>
          <Landmark className="w-3 h-3 mr-1" /> {meta.label}
        </Badge>
      </div>
      <div className="flex gap-2 mt-2">
        {cleaner.payout_status !== "ready" && (
          <Button size="sm" variant="outline" className="rounded-xl flex-1 text-xs" onClick={invite} disabled={busy}
            data-testid={`invite-payouts-${cleaner.id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Invite to set up payouts</>}
          </Button>
        )}
        {cleaner.stripe_account_id && (
          <Button size="sm" variant="outline" className="rounded-xl text-xs" onClick={refresh} disabled={busy}
            title="Re-read the account state from Stripe" data-testid={`refresh-payouts-${cleaner.id}`}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function ResendKitRow({ cleaner }) {
  const [busy, setBusy] = useState(false);

  const resend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/cleaners/${cleaner.id}/resend-welcome-kit`);
      const d = unwrap(r);
      toast.success(d?.sent
        ? `Welcome + kit email resent to ${cleaner.email}`
        : "Could not send the email (check the cleaner has an email on file)");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not resend the welcome + kit email");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2">
      <Button size="sm" variant="outline" className="rounded-xl w-full text-xs" onClick={resend} disabled={busy}
        data-testid={`resend-welcome-kit-${cleaner.id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1" /> Resend welcome + kit</>}
      </Button>
    </div>
  );
}

export function Avatar({ src, name, size = 40 }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (src) {
    return <img src={src} alt={name} width={size} height={size} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />;
  }
  return (
    <div style={{ width: size, height: size, fontSize: size / 2.8 }} className="rounded-full bg-[#0EA5A4]/15 text-[#0F766E] grid place-items-center font-bold shrink-0">
      {initials}
    </div>
  );
}

function CleanerForm({ cleaner, tasks, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY, ...(cleaner || {}),
    transportation_type: cleaner?.transportation_type || "",
    transportation_other: cleaner?.transportation_other || "",
  }));
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  if (!cleaner) return null;
  const isNew = !cleaner.id;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggle = (key, val) => set({ [key]: form[key].includes(val) ? form[key].filter((x) => x !== val) : [...form[key], val] });

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Photo must be under 2 MB"); return; }
    try { set({ photo_url: await fileToSquareDataUrl(file) }); } catch { toast.error("Could not read image"); }
  };

  const save = async () => {
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name, photo_url: form.photo_url, phone: form.phone, email: form.email,
        country: form.country, city: form.city, languages: form.languages, specialties: form.specialties,
        bio: form.bio, notes: form.notes, is_active: form.is_active,
        available_days: form.available_days, preferred_hours: form.preferred_hours,
        weekly_commitment: form.weekly_commitment, has_reliable_transportation: !!form.has_reliable_transportation,
        transportation_type: form.has_reliable_transportation ? (form.transportation_type || null) : null,
        transportation_other: form.has_reliable_transportation && form.transportation_type === "other" ? form.transportation_other : null,
      };
      if (isNew) await api.post("/admin/cleaners", payload);
      else await api.put(`/admin/cleaners/${cleaner.id}`, payload);
      toast.success(isNew ? "Cleaner added" : "Cleaner updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!cleaner} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cleaner-form-modal">
        <DialogHeader><DialogTitle className="font-display text-2xl tracking-tighter">{isNew ? "Add cleaner" : "Edit cleaner"}</DialogTitle></DialogHeader>

        <div className="flex items-center gap-4">
          <Avatar src={form.photo_url} name={form.full_name} size={80} />
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="cleaner-photo-input" />
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => fileRef.current?.click()} data-testid="cleaner-photo-btn"><Upload className="w-4 h-4 mr-2" /> Upload photo</Button>
            {form.photo_url && <Button variant="ghost" size="sm" className="rounded-xl text-rose-600" onClick={() => set({ photo_url: "" })}><X className="w-4 h-4" /></Button>}
          </div>
        </div>
        <p className="text-xs text-[#9CA3AF] -mt-2">Square crop, max 2 MB.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs">Full name *</Label>
            <Input value={form.full_name} onChange={(e) => set({ full_name: e.target.value })} className="rounded-xl mt-1" data-testid="cleaner-name-input" />
          </div>
          <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => set({ phone: e.target.value })} className="rounded-xl mt-1" data-testid="cleaner-phone-input" /></div>
          <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => set({ email: e.target.value })} className="rounded-xl mt-1" data-testid="cleaner-email-input" /></div>
          <div>
            <Label className="text-xs">Country</Label>
            <select value={form.country} onChange={(e) => set({ country: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="cleaner-country-input">
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name_en}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => set({ city: e.target.value })} className="rounded-xl mt-1" data-testid="cleaner-city-input" /></div>
        </div>

        <div>
          <Label className="text-xs">Languages</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {LANGUAGES.map((l) => (
              <Chip key={l.code} active={form.languages.includes(l.code)} onClick={() => toggle("languages", l.code)} testid={`lang-${l.code}`}>{l.label}</Chip>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs">Specialties</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {tasks.length === 0 ? <span className="text-xs text-[#9CA3AF]">No task catalog loaded</span> :
              tasks.map((tk) => (
                <Chip key={tk.code} active={form.specialties.includes(tk.code)} onClick={() => toggle("specialties", tk.code)} testid={`spec-${tk.code}`}>{tk.label_en || tk.code}</Chip>
              ))}
          </div>
        </div>

        <div>
          <Label className="text-xs">Bio (public — shown to customers)</Label>
          <Textarea rows={2} value={form.bio} onChange={(e) => set({ bio: e.target.value })} className="rounded-xl mt-1" placeholder="1–3 sentence introduction" data-testid="cleaner-bio-input" />
        </div>
        <div>
          <Label className="text-xs">Internal notes (admin only)</Label>
          <Textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} className="rounded-xl mt-1" data-testid="cleaner-notes-input" />
        </div>

        <div className="border-t border-[#E5E7EB] pt-4 space-y-3">
          <Label className="text-sm font-semibold">Availability & Transportation</Label>
          <div>
            <Label className="text-xs">Available days</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {DAYS.map((d) => (
                <Chip key={d.code} active={form.available_days.includes(d.code)} onClick={() => toggle("available_days", d.code)} testid={`cleaner-day-${d.code}`}>{d.label}</Chip>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Preferred hours</Label>
            <select value={form.preferred_hours} onChange={(e) => set({ preferred_hours: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="cleaner-preferred-hours-input">
              <option value="">Select…</option>
              {PREFERRED_HOURS.map((h) => <option key={h.code} value={h.code}>{h.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Weekly commitment</Label>
            <RadioGroup value={form.weekly_commitment} onValueChange={(v) => set({ weekly_commitment: v })} className="mt-1.5 space-y-1.5" data-testid="cleaner-commitment-input">
              {WEEKLY_COMMITMENT.map((c) => (
                <label key={c.code} className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer">
                  <RadioGroupItem value={c.code} data-testid={`cleaner-commitment-${c.code}`} /> {c.label}
                </label>
              ))}
            </RadioGroup>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-[#E5E7EB] px-4 py-3">
            <div><div className="text-sm font-semibold">Reliable transportation</div><div className="text-xs text-[#9CA3AF]">Has a reliable way to commute to jobs</div></div>
            <Switch checked={!!form.has_reliable_transportation} onCheckedChange={(v) => set({ has_reliable_transportation: v, transportation_type: v ? form.transportation_type : "" })} data-testid="cleaner-transport-switch" />
          </div>
          {form.has_reliable_transportation && (
            <div>
              <Label className="text-xs">Transportation type</Label>
              <RadioGroup value={form.transportation_type} onValueChange={(v) => set({ transportation_type: v })} className="mt-1.5 space-y-1.5" data-testid="cleaner-transport-type-input">
                {TRANSPORTATION_TYPE.map((t) => (
                  <label key={t.code} className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer">
                    <RadioGroupItem value={t.code} data-testid={`cleaner-transport-type-${t.code}`} /> {t.label}
                  </label>
                ))}
              </RadioGroup>
              {form.transportation_type === "other" && (
                <Input value={form.transportation_other} onChange={(e) => set({ transportation_other: e.target.value })} className="rounded-xl mt-2" placeholder="Describe transportation" data-testid="cleaner-transport-other-input" />
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between rounded-xl border border-[#E5E7EB] px-4 py-3">
          <div><div className="text-sm font-semibold">Active</div><div className="text-xs text-[#9CA3AF]">Inactive cleaners cannot be assigned</div></div>
          <Switch checked={form.is_active} onCheckedChange={(v) => set({ is_active: v })} data-testid="cleaner-active-switch" />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="cleaner-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Save cleaner</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-[#0EA5A4] text-white border-[#0EA5A4]" : "bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#0EA5A4]"}`}>
      {children}
    </button>
  );
}
